import useQuery from "utils/hooks/useQuery";
import { useEffect, useState } from "react";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import {
  BillingTab,
  SubscriptionQueryParams,
} from "features/settings/common/types";
import {
  BASE_BILLING_SETTINGS_PATH,
  BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  BILLING_SETTINGS_PATH,
  IS_WEB_VIEW,
  PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
} from "common/constants";
import { generatePath, useHistory, useRouteMatch } from "react-router-dom";
import {
  FormattedPlanDetails,
  SubscriptionPlanType,
  GroupData,
  LanguageNameSpaces,
  PurchaseSubscriptionType,
  SubscriptionDuration,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import moment from "moment";
import { dateIsLifetime } from "@shared/utils/date";
import JoinGroupFreeTrialModal from "features/newMarketplace/GroupExtraDetails/JoinGroup/JoinGroupFreeTrialModal";
import {
  PROMO_CODE_QUERY_PARAM,
  SMALL_MOBILE_CHECKOUT_HEIGHT,
} from "features/settings/common/constants";
import { getGroupSubscriptionExpiration } from "@shared/utils/misc";
import { getPurchaseSubscriptionPath } from "features/settings/common/utils";
import {
  SetDuration,
  SUBSCRIPTION_STEPS,
  SubscriptionParams,
  SubscriptionStep,
} from "@shared/features/settings/subscription/types";
import { useUser } from "@shared/utils/hooks/misc";
import {
  SUBSCRIPTION_QUERY_PARAMS,
  WEB_APPLE_PAY_SUBSCRIPTION_WARNING,
} from "@shared/features/settings/subscription/constants";
import {
  getParsedQueryPlan,
  getPlanErrorMessage,
  getWhitelabelPlanConflictError,
  validateQueryDuration,
  validateStep,
} from "@shared/features/settings/subscription/utils";
import useMedia from "utils/hooks/useMedia.ts";
import { openMessage } from "utils/misc";
import { useGetSubscriptionPlans } from "@shared/features/settings/subscription/hooks/misc.ts";
import { useDisplayPromoCode } from "features/settings/Subscription/hooks.ts";
import { MOBILE_BILLING_FEATURE_FLAG } from "@shared/common/constants.ts";

export const usePurchaseSubscriptionQueryParams = () => {
  const query = useQuery();
  const { user } = useUser();
  const purchaseSubscriptionMatch = useRouteMatch<{ subGroupId?: string }>(
    PURCHASE_SUBSCRIPTION_SETTINGS_PATH
  );
  const { subGroupId: subGroupIdStr } = purchaseSubscriptionMatch?.params || {};
  const queryIsAllPlans = !!(
    query.get(SUBSCRIPTION_QUERY_PARAMS.IS_ALL_PLANS) || ""
  );

  const getQueryDuration = () => {
    const queryDuration = query.get(SUBSCRIPTION_QUERY_PARAMS.DURATION) || "";
    if (validateQueryDuration(queryDuration)) {
      return parseInt(queryDuration) as SubscriptionDuration;
    }
  };

  const getQueryPlan = () => {
    const queryPlan = query.get(SUBSCRIPTION_QUERY_PARAMS.PLAN) || "";
    return getParsedQueryPlan(queryPlan);
  };

  const queryDuration = getQueryDuration();
  const queryPlan = getQueryPlan();
  const subGroupId = subGroupIdStr ? parseInt(subGroupIdStr) : undefined;
  const isUpgrade = !!query.get(SUBSCRIPTION_QUERY_PARAMS.IS_UPGRADE);
  const isExtend = (user?.isExtendSubscription() && !isUpgrade) || !!subGroupId;

  let defaultStep: SubscriptionStep = SUBSCRIPTION_STEPS.PLAN;
  if (subGroupId || (isExtend && !queryIsAllPlans))
    defaultStep = SUBSCRIPTION_STEPS.DURATION;
  let subscriptionParams: Partial<SubscriptionParams> = {
    step: defaultStep,
    paymentMethod:
      query.get(SUBSCRIPTION_QUERY_PARAMS.PAYMENT_METHOD) || undefined,
    isAllPlans: queryIsAllPlans,
  };

  if (queryDuration) {
    subscriptionParams.duration = queryDuration as SubscriptionDuration;
  }
  if (queryPlan) {
    subscriptionParams.plan = queryPlan;
  }
  const queryStep = query.get(SUBSCRIPTION_QUERY_PARAMS.STEP) || "";

  if (validateStep(queryStep, subscriptionParams, isUpgrade)) {
    subscriptionParams.step = queryStep;
  }
  if (!isUpgrade && !subscriptionParams.duration) {
    subscriptionParams.duration = subGroupId ? 30 : 360;
  }

  return {
    subscriptionParams: subscriptionParams as SubscriptionParams,
    subGroupId,
    isExtend,
    queryIsAllPlans,
    isUpgrade,
  };
};

export const usePurchaseSubscriptionQuery = () => {
  const history = useHistory();
  const query = useQuery();
  const { user } = useUser();
  const { appendQuery } = useHistoryWithQuery();
  const usePromoCode = useDisplayPromoCode({ user });
  const promoCode = usePromoCode
    ? query.get(PROMO_CODE_QUERY_PARAM)
    : undefined;

  const {
    subscriptionParams,
    subGroupId,
    isExtend,
    queryIsAllPlans,
    isUpgrade,
  } = usePurchaseSubscriptionQueryParams();
  const { plans, isLoading, isFetching } = useGetSubscriptionPlans(
    subGroupId,
    isUpgrade ? undefined : promoCode
  );

  const userHasLowerPackage =
    user?.subscription_plan_type &&
    subscriptionParams.plan &&
    subscriptionParams.plan !== "Group" &&
    user.subscription_plan_type < subscriptionParams.plan;

  useEffect(() => {
    if (!query.get(SUBSCRIPTION_QUERY_PARAMS.STEP)) {
      if (subGroupId) {
        setSubscriptionType("Group", true);
      } else if (isExtend && user?.subscription_plan_type && !queryIsAllPlans) {
        if (userHasLowerPackage) {
          history.push(getPurchaseSubscriptionPath({ isUpgrade: true }));
        } else if (subscriptionParams.plan !== user?.subscription_plan_type) {
          setSubscriptionType(user?.subscription_plan_type);
        }
      }
    } else if (subscriptionParams.duration !== 30 && !!subGroupId) {
      setDuration(30);
    }
  }, [query]);

  const showMobileAppWarning =
    user?.isNativeSubscription() &&
    isExtend &&
    !queryIsAllPlans &&
    !IS_WEB_VIEW;
  const planError = getPlanErrorMessage(
    plans,
    subscriptionParams.plan,
    showMobileAppWarning,
    user
  );
  useEffect(() => {
    if (planError) {
      openMessage(planError, "warning");
      history.push(BASE_BILLING_SETTINGS_PATH);
    }
  }, [planError]);

  const setSubscriptionType = (
    subscriptionType: PurchaseSubscriptionType,
    replace = false
  ) => {
    const query: Record<string, string> = {
      [SUBSCRIPTION_QUERY_PARAMS.PLAN]: subscriptionType.toString(),
      [SUBSCRIPTION_QUERY_PARAMS.STEP]: SUBSCRIPTION_STEPS.DURATION,
    };
    if (!isUpgrade && subscriptionParams.duration) {
      query.duration = subscriptionParams.duration.toString();
    }
    appendQuery(query, undefined, replace);
  };

  const setDuration: SetDuration = (
    subscriptionDuration: SubscriptionDuration,
    step?: SubscriptionStep
  ) => {
    const query: Record<string, string> = {
      [SUBSCRIPTION_QUERY_PARAMS.DURATION]: subscriptionDuration.toString(),
    };
    if (step) {
      query[SUBSCRIPTION_QUERY_PARAMS.STEP] = step;
    }
    appendQuery(query, undefined, false);
  };

  const setPaymentMethod = (paymentMethod: string) => {
    appendQuery(
      { [SUBSCRIPTION_QUERY_PARAMS.PAYMENT_METHOD]: paymentMethod },
      undefined,
      true
    );
  };

  const setPreviousQueryStep = (step: SubscriptionStep) => {
    const query = {
      [SUBSCRIPTION_QUERY_PARAMS.STEP]: step,
    };
    const keysToRemove: SubscriptionQueryParams[] = [];
    if (
      step === SUBSCRIPTION_STEPS.DURATION ||
      step === SUBSCRIPTION_STEPS.PLAN
    ) {
      keysToRemove.push(SUBSCRIPTION_QUERY_PARAMS.PAYMENT_METHOD);
    }
    if (step === SUBSCRIPTION_STEPS.PLAN) {
      keysToRemove.push(SUBSCRIPTION_QUERY_PARAMS.PLAN);
    }
    appendQuery(query, keysToRemove, false);
  };

  const previousStep = () => {
    const step = subscriptionParams.step;
    if (subGroupId || subscriptionParams.step === SUBSCRIPTION_STEPS.PLAN) {
      history.goBack();
    } else if (step === SUBSCRIPTION_STEPS.DURATION) {
      if (isExtend || isUpgrade) {
        if (queryIsAllPlans) {
          history.push(
            getPurchaseSubscriptionPath({ isAllPlans: true, promoCode })
          );
        } else {
          if (isUpgrade) {
            history.push(getPurchaseSubscriptionPath({ isUpgrade: true }));
          } else {
            history.push(BASE_BILLING_SETTINGS_PATH);
          }
        }
      } else {
        setPreviousQueryStep(SUBSCRIPTION_STEPS.PLAN);
      }
    }
  };

  return {
    subscriptionParams,
    setDuration,
    setSubscriptionType,
    setPaymentMethod,
    previousStep,
    isUpgrade,
    isExtend,
    subGroupId,
    plans,
    isFetching,
    isLoading,
  };
};

export const useGroupSubscriptionData = (
  groupData: GroupData,
  onCancelAutoRenewal?: () => void,
  cancelAutoRenewalText?: string
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { t: tBilling } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const [freeTrialModalOpen, setFreeTrialModalOpen] = useState(false);
  const history = useHistory();

  const onManageSubscription = () => {
    const billingPath = generatePath(BILLING_SETTINGS_PATH, {
      tab: BillingTab.GROUPS,
    });
    history.push(billingPath);
  };

  const onPurchaseSubscription = () => {
    history.push(
      generatePath(PURCHASE_SUBSCRIPTION_SETTINGS_PATH, {
        subGroupId: groupData.id,
      })
    );
  };

  const now = moment().unix();

  let buttonText: string | undefined;
  let buttonCallback: (() => void) | undefined;
  let showFreeTrialModal: boolean | undefined;
  if (groupData.next_auto_renewal) {
    buttonText = cancelAutoRenewalText || t("group_info.manage_subscription");
    buttonCallback = onCancelAutoRenewal || onManageSubscription;
  } else if (
    groupData.expiration_timestamp &&
    !dateIsLifetime(groupData.expiration_timestamp, true) &&
    groupData.support_payments
  ) {
    buttonText =
      groupData.expiration_timestamp > now
        ? t("group_info.extend_subscription")
        : t("group_info.purchase_subscription");
    buttonCallback = onPurchaseSubscription;
  } else if (groupData.entitled_to_free_trial) {
    buttonText = t("group_info.start_free_trial");
    buttonCallback = () => setFreeTrialModalOpen(true);
    showFreeTrialModal = true;
  }

  const freeTrialModal = showFreeTrialModal ? (
    <JoinGroupFreeTrialModal
      groupData={groupData}
      visible={freeTrialModalOpen}
      closeModal={() => setFreeTrialModalOpen(false)}
    />
  ) : undefined;

  return {
    actionButton:
      buttonText && buttonCallback
        ? {
            text: buttonText,
            onClick: buttonCallback,
          }
        : undefined,
    expirationText: getGroupSubscriptionExpiration(groupData, tBilling),
    freeTrialModal,
  };
};

export const useIsSmallMobileCheckout = () => {
  return useMedia(
    [`(max-height: ${SMALL_MOBILE_CHECKOUT_HEIGHT})`],
    [true],
    false
  );
};

export const useUpgradePlanPath = () => {
  const { user } = useUser();

  if (user?.isPurchaseSubscription())
    return BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH;
  return getPurchaseSubscriptionPath({ isUpgrade: true });
};

export const useHideBillingButtons = () => {
  const { user } = useUser();

  return IS_WEB_VIEW && !user?.hasFeatureFlag(MOBILE_BILLING_FEATURE_FLAG);
};

export const useCancelAutoRenewalWarning = () => {
  const { user } = useUser();

  if (user && user.has_auto_renewal && user.isApplePaySubscription()) {
    return "You can manage or cancel your Apple subscription from your App Store account settings or by visiting apps.apple.com/account/subscriptions.";
  }
};

interface useGeneralPaymentErrorsProps {
  plan?: FormattedPlanDetails;
}
export const useGeneralPaymentErrors = ({
  plan,
}: useGeneralPaymentErrorsProps) => {
  const { user } = useUser();

  if (user?.isApplePaySubscription() && !IS_WEB_VIEW) {
    return WEB_APPLE_PAY_SUBSCRIPTION_WARNING;
  }
  const whitelabelConflictError = getWhitelabelPlanConflictError(
    user?.subscription_plan_type,
    plan?.subscription_plan_type
  );
  if (whitelabelConflictError) {
    return whitelabelConflictError;
  }
  if (plan?.subscription_plan_type === SubscriptionPlanType.FREE) {
    if (user?.isFree()) {
      return "You’re already on the free plan.";
    } else {
      return user?.has_auto_renewal
        ? "Please cancel your auto-renewing subscription first. Once it expires, your account will automatically switch to the free plan."
        : "Please wait until your subscription expires, and your account will automatically switch to the free plan.";
    }
  }
};
